import { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/Button';
import { TextField } from '@/components/TextField';
import { resendVerification, AuthActionError } from '@/lib/auth-actions';
import { useVerifyPolling } from '@/hooks/useVerifyPolling';

const RESEND_COOLDOWN_S = 60;

export function VerifyPage() {
  const { t } = useTranslation(['auth']);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const urlEmail = params.get('email') ?? '';
  const redirectParam = params.get('redirect');
  const errorCode = params.get('error');
  const isExpired = errorCode === 'otp_expired';

  // In error mode the email may not be in the URL (user landed via a bare
  // hash redirect from useAuthErrorHandler). Expose a controlled input so
  // the user can supply it. In the happy path the URL email is canonical
  // and the input is hidden — `email` is read directly from the param.
  const [emailInput, setEmailInput] = useState(urlEmail);
  const [cooldown, setCooldown] = useState(0);
  const [resending, setResending] = useState(false);

  // Polling polls auth.users.email_confirmed_at — useless in error mode and
  // would just spam the network. Disabled when ?error= is present.
  const { verified } = useVerifyPolling(!isExpired);

  useEffect(() => {
    if (verified) {
      navigate(
        redirectParam
          ? `/verify/success?redirect=${encodeURIComponent(redirectParam)}`
          : '/verify/success',
        { replace: true },
      );
    }
  }, [verified, navigate, redirectParam]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const resendEmail = isExpired ? emailInput.trim() : urlEmail;

  const handleResend = async () => {
    if (!resendEmail) return;
    setResending(true);
    try {
      await resendVerification(resendEmail);
      toast.success(t('auth:verify.resendDone'));
      setCooldown(RESEND_COOLDOWN_S);
    } catch (error) {
      if (error instanceof AuthActionError && error.code === 'rate_limited') {
        toast.error(t('auth:errors.rateLimited'));
      } else {
        toast.error(t('auth:errors.generic'));
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <section className="m5-shell">
      <div className="m5-icon-badge" aria-hidden="true">
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M4 4h16v16H4z" />
          <path d="m4 6 8 7 8-7" />
        </svg>
      </div>

      {isExpired ? (
        <>
          <h1 className="m5-shell__title">{t('auth:verify.expiredTitle')}</h1>
          <p className="m5-shell__lead">{t('auth:verify.expiredLead')}</p>
        </>
      ) : (
        <>
          <h1 className="m5-shell__title">{t('auth:verify.waitingTitle')}</h1>
          <p className="m5-shell__lead">
            <Trans
              i18nKey="auth:verify.waitingLead"
              values={{ email: urlEmail }}
              components={{ strong: <strong /> }}
            />
          </p>
        </>
      )}

      <div className="m5-card">
        {isExpired ? (
          <TextField
            type="email"
            name="email"
            label={t('auth:fields.email')}
            value={emailInput}
            onChange={(event) => setEmailInput(event.target.value)}
            placeholder={t('auth:verify.expiredEmailPrompt')}
            autoComplete="email"
          />
        ) : (
          <Button
            variant="primary"
            type="button"
            onClick={() => {
              window.open('https://mail.google.com', '_blank', 'noopener,noreferrer');
            }}
          >
            {t('auth:verify.openMailbox')}
          </Button>
        )}

        <Button
          variant={isExpired ? 'primary' : 'outlined'}
          type="button"
          loading={resending}
          loadingLabel={t('auth:common.loading')}
          disabled={cooldown > 0 || !resendEmail}
          onClick={handleResend}
        >
          {cooldown > 0
            ? t('auth:verify.resendIn', { seconds: cooldown })
            : t('auth:verify.resend')}
        </Button>

        <p className="m5-footnote">
          {t('auth:verify.wrongEmail')}{' '}
          <Link to="/signup" className="m5-link">
            {t('auth:verify.backToSignUp')}
          </Link>
        </p>
      </div>
    </section>
  );
}
